import { useEffect } from "react";
import Logging from "logging";

import { PassportFieldName } from "../../../../../models/documents/international-passport";
import { useAppStore } from "../../../../../store/store";
import { getDateWithoutTime } from "utils";
import Pax from "models/pax";
import Checklist from "models/checklist/checklist";
import PassportType, {
  PassportTypeToCode,
} from "models/documents/passport-type";

const useUpdatePassportFromChecklist = (
  paxSessionId?: string,
  pax?: Pax,
  checklist?: Checklist
) => {
  useEffect(() => {
    // If the form currently holds another passenger's data, clear it before
    // prefilling this passenger's. The owner marker lives in the store (not a
    // component ref) so this also works across remounts of the passport page -
    // e.g. navigating out via "All passengers" and into a different passenger,
    // which unmounts and remounts this hook. A same-passenger remount (e.g.
    // leaving to scan and coming back) keeps the values, since the owner still
    // matches, so we never wipe data just entered for the current passenger.
    if (
      paxSessionId !== undefined &&
      useAppStore.getState().passportValuesPaxSessionId !== paxSessionId
    ) {
      useAppStore.getState().resetPassportValues(paxSessionId);
    }

    const passportValues = useAppStore.getState().passportValues;

    if (!passportValues.givenName && pax?.firstName) {
      useAppStore.getState().changePassportValue(PassportFieldName.givenName)(
        pax.firstName
      );
    }

    if (!passportValues.surname && pax?.surname) {
      useAppStore.getState().changePassportValue(PassportFieldName.surname)(
        pax.surname
      );
    }

    if (!passportValues.birthdate && checklist?.birthdate) {
      useAppStore.getState().changePassportValue(PassportFieldName.birthdate)(
        getDateWithoutTime(checklist.birthdate)
      );
    }

    if (!passportValues.nationality && checklist?.nationality) {
      useAppStore.getState().changePassportValue(PassportFieldName.nationality)(
        checklist.nationality
      );
    }

    if (!passportValues.countryCode && checklist?.nationality) {
      useAppStore.getState().changePassportValue(PassportFieldName.countryCode)(
        checklist.nationality
      );
    }

    // check if there is PassportTypeIn question in checklist and it was answered
    const passportTypeFromQuestions = checklist?.questions.find(
      (question) =>
        question.type === "pick-from-list-question" &&
        question.id === "PassportTypeInQuestion"
    )?.answer;

    if (
      !passportValues.passportType &&
      passportTypeFromQuestions !== undefined
    ) {
      // if passport type was not already set in the passport manual form
      // and there is an answer from questions - set it as default,
      // as it has a higher priority then a "default" passport type value
      try {
        useAppStore
          .getState()
          .changePassportValue(PassportFieldName.passportType)(
          PassportTypeToCode(passportTypeFromQuestions as PassportType)
        );
      } catch (e) {
        Logging.log(
          "checklist",
          paxSessionId || "missing",
          `Can't convert passportType '${passportTypeFromQuestions}' to code: ${
            (e as Error).message
          }`
        );
      }
      return;
    }

    if (!passportValues.passportType) {
      // passport type was not set in the passport manual form
      // AND it was not part of questions (or was not yet answered somehow)
      useAppStore
        .getState()
        .changePassportValue(PassportFieldName.passportType)(
        PassportTypeToCode(PassportType.Ordinary)
      );
      return;
    }
  }, [paxSessionId, pax, checklist]);
};

export default useUpdatePassportFromChecklist;
